import React, { useState, useEffect } from 'react';
import Tree from 'rc-tree';
import 'rc-tree/assets/index.css';
import './GroupManagement.css';
import deleteIcon from '../../../assets/icons/common/delete.svg';
import { FormattedMessage } from 'react-intl';
import API from '../../../utils/API';

const GroupTree = ({ groupsTreeData, checkedKey, setCheckedKey, selectedKey, setSelectedKey, handleDeleteGroup, setError }) => {

    const [editingKey, setEditingKey] = useState(null);
    const [editingValue, setEditingValue] = useState('');
    const [lastSelectedKey, setLastSelectedKey] = useState(null);
    const [treeData, setTreeData] = useState([]);

    const handleEdit = (key, title) => {
        if (selectedKey.includes(key)) {
            setEditingKey(key);
            setEditingValue(title);
        } else {
            setSelectedKey([key]);
        }
    };

    useEffect(() => {
        setTreeData(groupsTreeData);
    }, [groupsTreeData]);

    const resetEditingState = () => {
        setEditingKey(null);
        setEditingValue('');
    };

    const handleSave = async (key) => {
        if (editingValue.length < 2 || editingValue.length > 36) {
            resetEditingState();
            setError(<FormattedMessage id='GROUP_NAME_LENGTH' />);
            return;
        }

        const originalTitle = treeData.find(item => item.key === key).title;
        if (editingValue === originalTitle) {
            resetEditingState();
            return;
        }

        try {
            await API.updateGroup({ id: key, name: editingValue });

            const updateTreeData = (data) => 
                data.map(item => item.key === key ? { ...item, title: editingValue } : item);
            setTreeData(updateTreeData(treeData));
        } catch (error) {
            setError(<FormattedMessage id='EDIT_GROUP_FAILED' values={{ errorMsg: <b>{error.message}</b> }}/>);
            console.error('Failed to update group:', error);
        } finally {
            resetEditingState();
        }
    }

    const handleCheck = (keys, { node, nativeEvent }) => {
        nativeEvent.stopPropagation();
        if (nativeEvent.ctrlKey) {
            setCheckedKey((prevCheckedKey) => {
                if (prevCheckedKey.includes(node.key)) {
                    return prevCheckedKey.filter((key) => key !== node.key);
                } else {
                    return [...prevCheckedKey, node.key];
                }
            });
        } else if (nativeEvent.shiftKey && lastSelectedKey) {
            const startIndex = treeData.findIndex(item => item.key === lastSelectedKey);
            const endIndex = treeData.findIndex(item => item.key === node.key);
            const range = [startIndex, endIndex].sort((a, b) => a - b);
            const newKeys = treeData.slice(range[0], range[1] + 1).map(item => item.key);
            setCheckedKey([...new Set([...keys, ...newKeys])]);
        } else {
            setCheckedKey(keys);
        }
        setLastSelectedKey(node.key);
    };

    const handleBlur = (key) => {
        handleSave(key);
    };

    const handleClickOutside = (event) => {
        if (editingKey && !event.target.closest('.editing-input')) {
            handleSave(editingKey);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [editingKey, editingValue]);

    const handleSelect = (selectedKeys, { node, nativeEvent }) => {
        nativeEvent.stopPropagation();
        if (selectedKey === '-1' || selectedKey === '-2') {
            setSelectedKey([node.key]);
            setLastSelectedKey(node.key);
            return; 
        }
        if (nativeEvent.ctrlKey) {
            setSelectedKey((prevSelectedKeys) => {
                if (prevSelectedKeys.includes(node.key)) {
                    return prevSelectedKeys.filter((key) => key !== node.key);
                } else {
                    return [...prevSelectedKeys, node.key];
                }
            });
        } else if (nativeEvent.shiftKey && lastSelectedKey) {
            const startIndex = treeData.findIndex(item => item.key === lastSelectedKey);
            const endIndex = treeData.findIndex(item => item.key === node.key);
            const range = [startIndex, endIndex].sort((a, b) => a - b);
            const newSelectedTextKeys = treeData.slice(range[0], range[1] + 1).map(item => item.key);
            setSelectedKey([...new Set([...selectedKey, ...newSelectedTextKeys])]);
        } else {
            setSelectedKey([node.key]);
        }
        setLastSelectedKey(node.key);
    };

    const handleDelete = async (e,key) => {
        e.stopPropagation();
        await handleDeleteGroup(key);
    };

    const renderTreeNodes = (data) =>
        Array.isArray(data) ? data.map((item) => {
            const isSelected = selectedKey.includes(item.key);
            const nodeClassName = isSelected ? 'selected-node' : '';
            const treeNodeClassName = isSelected ? 'rc-tree-treenode-selected' : '';

            if (item.key === editingKey) {
                return {
                    title: (
                            <input
                                className="editing-input"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onBlur={() => handleBlur(item.key)}
                            />
                    ),
                    key: item.key,
                    dataRef: item,
                    className: treeNodeClassName,
                };
            }
            return {
                title: (
                    <div className={`node-content ${nodeClassName}`} title={item.title}>
                        <span className="node-text"
                            onClick={(e) => {
                                if (!e.ctrlKey && !e.metaKey) {
                                    handleEdit(item.key, item.title);
                                }
                                handleSelect([item.key], { node: item, nativeEvent: e });
                            }}
                        >
                            {item.title}
                        </span>
                        <button className="node-button" onClick={(e) => handleDelete(e,item.key)}>
                            <img src={deleteIcon} alt="Custom Icon" className="custom-icon" />
                        </button>
                    </div>
                ),
                key: item.key,
                dataRef: item,
                checkable: true,
                switcherIcon: null, 
                className: treeNodeClassName,
            };
        }) : [];

    return (
        <>
            <div className="link-container " >
                <span
                    className={`text-decoration-none cursor-pointer all-receivers-option ${selectedKey === '-2' ? 'selected-option' : ''}`}
                    onClick={(e) => setSelectedKey('-2')}
                >
                    <FormattedMessage id="VIEW_UNASSIGNED_RECEIVERS" defaultMessage="View Unassigned Receivers" />
                </span>
            </div>
            <div className="link-container" >
                <span
                    className={`text-decoration-none cursor-pointer all-receivers-option ${selectedKey === '-1' ? 'selected-option' : ''}`}
                    onClick={(e) => setSelectedKey('-1')}
                >
                    <FormattedMessage id="ALL_RECEIVERS" defaultMessage="All Receivers" />
                </span>
            </div>
            <div className='scrollable-div'>
                <Tree
                    checkable
                    onCheck={handleCheck}
                    checkedKeys={checkedKey}
                    onSelect={handleSelect}
                    selectedKeys={selectedKey==='-1'|| selectedKey==='-2' ? [] : selectedKey}
                    showIcon={false}
                    treeData={renderTreeNodes(treeData)}
                />
            </div>
        </>
    );
};

export default GroupTree;
